// This is the widget displaying all the conversation pairs.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Analytics } from '../analytics';
import { CHAT_CONFIG, type ExpandedLayout } from '../config/constants';
import { useChatLanguage } from '../context/ChatThemeContext';
import type { ConversationPair as ConversationPairType } from '../services/chatStorage';
import ConversationPair, { isHiddenMarker } from './ConversationPair';

interface StreamingMessage {
    id: string;
    content: string;
    isComplete: boolean;
    isThinking: boolean;
    suggestedFollowUps?: string[];
    suggestedAnswers?: string[];
}

interface ChatMessagesProps {
    conversationPairs: ConversationPairType[];
    streamingMessage?: StreamingMessage;
    isLoading: boolean;
    primaryColor: string;
    siteConfig: { name: string };
    messagesEndRef: React.RefObject<HTMLDivElement>;
    evaluationEnabled?: boolean;
    domain: string;
    onLatestQuestionRef?: (ref: HTMLElement | null) => void;
    onFollowUpClick?: (question: string) => void;
    onSuggestedAnswerClick?: (answer: string) => void;
    onRetry?: () => void;
    hideInlineCTAs?: boolean;
    viewLayout?: ExpandedLayout;
    // Inline demo CTA props (shown when 👇 emoji detected in last message)
    showInlineDemoCTA?: boolean;
    isCtaPage?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    /** Height of content rendered after ChatMessages (e.g. calendar embed). When set, the spacer is sized so that content can scroll to the top of the viewport. */
    trailingContentHeight?: number;
    /** Space already reserved at the bottom of the scroll container (e.g. overlaid input shell). */
    bottomInset?: number;
    /** Changes when the fixed chat viewport resizes, causing spacer geometry to refresh. */
    viewportResizeKey?: string;
    hideInlineProfilingProgress?: boolean;
    /** Whether post-conversion qualification mode is active (enables funnel tracking). */
    isPostConversion?: boolean;
}

const WELCOME_MESSAGES = {
    en: "👋 Welcome!\nI'm here to help you get the most out of {{company_name}}. Ask me anything — from product features to tailored recommendations, I'm just a message away.",
    fr: '👋 Bienvenue !\nJe suis là pour vous aider à tirer le meilleur parti de {{company_name}}. Posez-moi toutes vos questions, des fonctionnalités aux recommandations personnalisées.',
    de: '👋 Willkommen!\nIch helfe Ihnen dabei, {{company_name}} optimal zu nutzen. Fragen Sie mich alles – von Produktfunktionen bis zu individuellen Empfehlungen.',
    es: '👋 ¡Bienvenido!\nEstoy aquí para ayudarle a sacar el máximo partido de {{company_name}}. Pregúnteme lo que quiera, desde funciones del producto hasta recomendaciones personalizadas.',
    it: '👋 Benvenuto!\nSono qui per aiutarti a ottenere il massimo da {{company_name}}. Chiedimi qualsiasi cosa, dalle funzionalità del prodotto ai consigli personalizzati.',
    pt: '👋 Bem-vindo!\nEstou aqui para ajudar a tirar o máximo partido de {{company_name}}. Pergunte o que quiser, desde funcionalidades até recomendações personalizadas.',
    bg: '👋 Добре дошли!\nТук съм, за да ви помогна да извлечете максимума от {{company_name}}. Питайте ме всичко — от функции на продукта до персонализирани препоръки.',
    nl: '👋 Welkom!\nIk ben er om je te helpen het meeste uit {{company_name}} te halen. Vraag me alles, van productfuncties tot persoonlijke aanbevelingen.',
    ro: '👋 Bine ați venit!\nSunt aici pentru a vă ajuta să profitați la maximum de {{company_name}}. Întrebați-mă orice, de la funcționalități ale produsului la recomandări personalizate.',
    hu: '👋 Üdvözöljük!\nAzért vagyok itt, hogy segítsek a legtöbbet kihozni a(z) {{company_name}} használatából. Kérdezzen bátran bármit, a termékfunkcióktól a személyre szabott ajánlásokig.',
} as const;

const ChatMessages: React.FC<ChatMessagesProps> = ({
    conversationPairs,
    streamingMessage,
    isLoading,
    primaryColor,
    siteConfig,
    messagesEndRef,
    evaluationEnabled = true,
    domain,
    onLatestQuestionRef,
    onFollowUpClick,
    onSuggestedAnswerClick,
    onRetry,
    hideInlineCTAs,
    viewLayout = 'expanded',
    showInlineDemoCTA,
    isCtaPage,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
    trailingContentHeight,
    bottomInset = 0,
    viewportResizeKey,
    hideInlineProfilingProgress,
    isPostConversion = false,
}) => {
    const language = useChatLanguage();
    const questionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
    const previousQuestionCountRef = useRef(0);
    const lastPairRef = useRef<HTMLDivElement>(null);
    const [spacerHeight, setSpacerHeight] = useState(0);
    // Track the last profiling question number we fired an event for (dedup)
    const lastTrackedQuestionRef = useRef(0);
    const hasTrackedCompletionRef = useRef(false);

    // Stable callback for question ref tracking — avoids creating new function per render per pair
    const handleQuestionRef = useCallback((id: string, ref: HTMLElement | null) => {
        if (id) {
            questionRefs.current[id] = ref;
        }
    }, []);

    // Memoize display pairs to avoid unnecessary re-renders of memoized ConversationPair
    const displayPairs = useMemo(() => {
        const pairs = [...conversationPairs];

        // Add streaming pair if we have a streaming message
        if (streamingMessage) {
            const lastPair = pairs[pairs.length - 1];
            if (lastPair && !lastPair.answer) {
                pairs[pairs.length - 1] = {
                    ...lastPair,
                    answer: {
                        id: streamingMessage.id,
                        content: streamingMessage.content,
                        role: 'assistant' as const,
                        timestamp: Date.now(),
                        isEvaluated: false,
                        isStreaming: true,
                        isThinking: streamingMessage.isThinking,
                    },
                };
            }
        }

        return pairs;
    }, [conversationPairs, streamingMessage]);

    const currentQuestionCount = conversationPairs.length;

    // Padding from top of viewport when scrolling question to top
    const TOP_PADDING = 16;

    // Calculate spacer synchronously after DOM update
    // biome-ignore lint/correctness/useExhaustiveDependencies: getScrollContainer is a stable local helper; streamingMessage?.content is an intentional trigger for spacer recalc during streaming
    useLayoutEffect(() => {
        // Don't add spacer when the last question is a hidden marker — there's no
        // visible question to scroll-to-top, so the spacer would just create a gap.
        if (isHiddenMarker(conversationPairs[conversationPairs.length - 1]?.question?.content)) {
            setSpacerHeight(0);
            return;
        }

        if (lastPairRef.current && messagesEndRef.current) {
            // When trailing content (e.g. calendar embed) is rendered after ChatMessages
            // in the scroll container, set spacer to 0 — the trailing content itself
            // provides the scroll height needed.
            if (trailingContentHeight != null) {
                setSpacerHeight(0);
                return;
            }
            const scrollableParent = messagesEndRef.current?.closest(
                '.chat-messages-container'
            ) as HTMLDivElement | null;
            if (scrollableParent) {
                const viewportHeight = Math.max(0, scrollableParent.clientHeight - bottomInset);
                const lastPairHeight = lastPairRef.current.offsetHeight;
                const newSpacerHeight = Math.max(0, viewportHeight - lastPairHeight - TOP_PADDING);
                setSpacerHeight(newSpacerHeight);
            }
        }
    }, [
        conversationPairs,
        streamingMessage?.content,
        messagesEndRef,
        trailingContentHeight,
        bottomInset,
        viewportResizeKey,
    ]);

    // Scroll to latest question when a new question is added - positions question at top with padding
    useEffect(() => {
        if (currentQuestionCount > previousQuestionCountRef.current && displayPairs.length > 0) {
            const latestPair = displayPairs[displayPairs.length - 1];
            if (latestPair.question) {
                // Hidden markers (booking completed, etc.) have no visible question —
                // scroll to the bottom so the answer is visible.
                if (isHiddenMarker(latestPair.question.content)) {
                    setTimeout(() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 150);
                } else {
                    setTimeout(() => {
                        const latestQuestionRef = questionRefs.current[latestPair.question.id];
                        if (latestQuestionRef) {
                            const scrollableParent = messagesEndRef.current?.closest(
                                '.chat-messages-container'
                            ) as HTMLDivElement | null;
                            if (scrollableParent) {
                                const parentRect = scrollableParent.getBoundingClientRect();
                                const questionRect = latestQuestionRef.getBoundingClientRect();
                                const currentScrollTop = scrollableParent.scrollTop;
                                // Scroll so question is at top with TOP_PADDING pixels of space
                                const targetScrollTop =
                                    currentScrollTop +
                                    (questionRect.top - parentRect.top) -
                                    TOP_PADDING;
                                scrollableParent.scrollTo({
                                    top: targetScrollTop,
                                    behavior: 'smooth',
                                });
                            }
                        }
                    }, 150);
                }
            }
        }
        previousQuestionCountRef.current = currentQuestionCount;
    }, [currentQuestionCount, messagesEndRef, displayPairs]);

    // Update the latest question ref when component updates
    useEffect(() => {
        if (displayPairs.length > 0 && onLatestQuestionRef) {
            const latestPair = displayPairs[displayPairs.length - 1];
            if (latestPair.question) {
                const latestQuestionRef = questionRefs.current[latestPair.question.id];
                onLatestQuestionRef(latestQuestionRef);
            }
        }
    }, [displayPairs, onLatestQuestionRef]);

    // FIXME(IX-2515): Post-qualification completion state is derived from chat message
    // metadata (profilingProgress.is_complete on AssistantMessage). The backend already
    // knows when the form is complete — it should own this state on the conversation/session
    // and fire the analytics event server-side, instead of relying on the frontend to
    // read message metadata. See https://linear.app/inboundx/issue/IX-2515
    useEffect(() => {
        if (!isPostConversion) return;
        const lastPair = conversationPairs[conversationPairs.length - 1];
        const progress = lastPair?.answer?.profilingProgress;
        if (!progress || lastPair?.answer?.isStreaming) return;

        if (progress.is_complete && !hasTrackedCompletionRef.current) {
            hasTrackedCompletionRef.current = true;
            Analytics.trackPostQualificationCompleted({
                totalQuestions: progress.total_fields,
                messageCount: conversationPairs.length,
            });
        } else if (
            !progress.is_complete &&
            progress.current_question > lastTrackedQuestionRef.current
        ) {
            lastTrackedQuestionRef.current = progress.current_question;
            const progressPercent =
                progress.total_fields > 0
                    ? Math.round((progress.current_question / progress.total_fields) * 100)
                    : 0;
            Analytics.trackPostQualificationQuestionDisplayed({
                questionNumber: progress.current_question,
                totalQuestions: progress.total_fields,
                progressPercent,
            });
        }
    }, [isPostConversion, conversationPairs]);

    if (conversationPairs.length === 0 && !streamingMessage) {
        const [welcomeTitle, welcomeDescription] = WELCOME_MESSAGES[language]
            .replace('{{company_name}}', siteConfig.name)
            .split('\n');

        // Welcome message
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    gap: '20px',
                    maxWidth: '800px',
                    margin: '0 auto',
                }}
            >
                <p
                    style={{
                        fontWeight: 500,
                        fontSize: '20px',
                        textAlign: 'center',
                        color: CHAT_CONFIG.COLORS.TEXT.SECONDARY,
                        margin: 0,
                        maxWidth: '600px',
                        wordWrap: 'break-word',
                    }}
                >
                    {welcomeTitle}
                    <br />
                    {welcomeDescription}
                </p>
            </div>
        );
    }

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                maxWidth: '800px',
                margin: '0 auto',
            }}
        >
            {displayPairs.map((pair, index) => {
                const isLastPair = index === displayPairs.length - 1;
                const suggestedFollowUps =
                    isLastPair && pair.answer?.suggestedFollowUps
                        ? pair.answer.suggestedFollowUps
                        : undefined;
                const suggestedAnswers =
                    isLastPair && pair.answer?.suggestedAnswers
                        ? pair.answer.suggestedAnswers
                        : undefined;
                const profilingProgress =
                    isLastPair && pair.answer?.profilingProgress
                        ? pair.answer.profilingProgress
                        : undefined;

                const conversationPairElement = (
                    <ConversationPair
                        pair={{
                            question: pair.question,
                            answer: pair.answer,
                        }}
                        index={index}
                        primaryColor={primaryColor}
                        evaluationEnabled={evaluationEnabled}
                        isLoading={isLoading}
                        isLastPair={isLastPair}
                        domain={domain}
                        onQuestionRef={handleQuestionRef}
                        suggestedFollowUps={suggestedFollowUps}
                        suggestedAnswers={suggestedAnswers}
                        profilingProgress={profilingProgress}
                        onFollowUpClick={onFollowUpClick}
                        onSuggestedAnswerClick={onSuggestedAnswerClick}
                        onRetry={onRetry}
                        hideInlineCTAs={hideInlineCTAs}
                        viewLayout={viewLayout}
                        showInlineDemoCTA={isLastPair && showInlineDemoCTA}
                        isCtaPage={isCtaPage}
                        inlineCtaOptionEnabled={inlineCtaOptionEnabled}
                        visitorEmail={visitorEmail}
                        onBookingFlowStart={onBookingFlowStart}
                        hideInlineProfilingProgress={hideInlineProfilingProgress}
                    />
                );

                // Wrap the last pair with a ref to measure its height for dynamic spacer
                if (isLastPair) {
                    return (
                        <div key={pair.id} ref={lastPairRef}>
                            {conversationPairElement}
                        </div>
                    );
                }

                return <React.Fragment key={pair.id}>{conversationPairElement}</React.Fragment>;
            })}

            <div style={{ flexShrink: 0 }}>
                {/* Dynamic spacer to allow question to scroll to top without extra scrollable whitespace */}
                <div style={{ minHeight: spacerHeight, flexShrink: 0 }} />

                {/* Invisible element to scroll to */}
                <div ref={messagesEndRef} />
            </div>
        </div>
    );
};

export default ChatMessages;
